import { useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useT } from "@/lib/i18n";
import { HOME } from "@/constants/testIds";

const LOGO_HERO =
    "https://customer-assets-jai6qajn.emergentagent.net/job_fiesta-store-pro/artifacts/0zyub1xw_232849.png";
const HERO_VIDEO_MP4 = "/media/hero.mp4";

const RevealLine = ({ children, delay = 0 }) => (
    <motion.span
        initial={{ opacity: 0, y: 40, filter: "blur(6px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ delay: 1.6 + delay, duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
        className="block will-change-transform"
    >
        {children}
    </motion.span>
);

// Floating decorative element — has intro, gentle float, parallax on scroll
const FloatDecor = ({ children, className, style, delay = 2.2, yShift = -60, xDrift = 0, rotShift = 0, floatDur = 6, scrollY }) => {
    const y = useTransform(scrollY, [0, 1], [0, yShift]);
    const x = useTransform(scrollY, [0, 1], [0, xDrift]);
    const rotate = useTransform(scrollY, [0, 1], [0, rotShift]);
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.4, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            style={{ y, x, rotate, ...style }}
            className={`absolute pointer-events-none ${className || ""}`}
        >
            <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: floatDur, repeat: Infinity, ease: "easeInOut", delay: delay * 0.3 }}
            >
                {children}
            </motion.div>
        </motion.div>
    );
};

const Hero = ({ settings }) => {
    const { lang } = useLang();
    const t = useT(lang);
    const ref = useRef(null);
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);

    const toggleMute = async () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !muted;
        v.muted = next;
        // browsers require a user gesture to play with sound; also handle blocked autoplay
        try {
            if (v.paused) await v.play();
        } catch (_e) {
            /* ignore */
        }
        setMuted(next);
    };
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

    // Scroll parallax
    const yBanner = useTransform(scrollYProgress, [0, 1], [0, -140]);
    const rotBanner = useTransform(scrollYProgress, [0, 1], [0, 8]);
    const scaleBanner = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
    const opacityText = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

    // Mouse-tracking 3D tilt on the banner
    const mvX = useMotionValue(0);
    const mvY = useMotionValue(0);
    const springOpts = { stiffness: 120, damping: 15, mass: 0.6 };
    const rotX = useSpring(useTransform(mvY, [-0.5, 0.5], [10, -10]), springOpts);
    const rotY = useSpring(useTransform(mvX, [-0.5, 0.5], [-14, 14]), springOpts);
    const glareX = useTransform(mvX, [-0.5, 0.5], ["10%", "90%"]);
    const glareY = useTransform(mvY, [-0.5, 0.5], ["10%", "90%"]);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        mvX.set(px);
        mvY.set(py);
    };
    const handleMouseLeave = () => {
        mvX.set(0);
        mvY.set(0);
    };

    const lines = useMemo(
        () =>
            lang === "es"
                ? [settings?.hero_line1_es, settings?.hero_line2_es, settings?.hero_line3_es]
                : [settings?.hero_line1_en, settings?.hero_line2_en, settings?.hero_line3_en],
        [lang, settings],
    );
    const kicker = lang === "es" ? settings?.hero_kicker_es : settings?.hero_kicker_en;
    const tagline = lang === "es" ? settings?.tagline_es : settings?.tagline_en;

    return (
        <section
            ref={ref}
            className="relative w-full min-h-[100svh] pt-24 md:pt-28 pb-10 overflow-hidden"
            style={{ background: "linear-gradient(180deg, #FFF6EE 0%, #FAF8F5 55%, #FFF0F5 100%)" }}
        >
            {/* Ambient blur blobs */}
            <motion.div
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 2 }}
                className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl"
                style={{ background: "radial-gradient(circle, rgba(232,58,116,0.28), transparent 60%)" }}
            />
            <motion.div
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 2 }}
                className="pointer-events-none absolute -bottom-40 -left-24 w-[480px] h-[480px] rounded-full blur-3xl"
                style={{ background: "radial-gradient(circle, rgba(139,95,191,0.22), transparent 60%)" }}
            />

            {/* Confetti dots */}
            <div className="absolute inset-0 pointer-events-none">
                <motion.span animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity }} className="absolute top-[18%] left-[6%] w-2 h-2 rounded-full dot-pink" />
                <motion.span animate={{ y: [0, -14, 0] }} transition={{ duration: 6, repeat: Infinity, delay: 0.3 }} className="absolute top-[26%] left-[12%] w-3 h-3 rounded-full dot-teal" />
                <motion.span animate={{ y: [0, -8, 0] }} transition={{ duration: 4.5, repeat: Infinity, delay: 0.8 }} className="absolute top-[62%] left-[8%] w-2 h-2 rounded-full dot-gold" />
                <motion.span animate={{ y: [0, -12, 0] }} transition={{ duration: 5.5, repeat: Infinity, delay: 1.2 }} className="absolute top-[80%] left-[22%] w-2 h-2 rounded-full dot-purple" />
                <motion.span animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, delay: 0.6 }} className="absolute top-[15%] right-[10%] w-3 h-3 rounded-full dot-sky" />
                <motion.span animate={{ y: [0, -14, 0] }} transition={{ duration: 5, repeat: Infinity, delay: 1.5 }} className="absolute top-[45%] right-[6%] w-2 h-2 rounded-full dot-pink" />
                <motion.span animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 0.9 }} className="absolute top-[72%] right-[16%] w-2 h-2 rounded-full dot-gold" />
            </div>

            <motion.div style={{ opacity: opacityText }} className="relative mx-auto max-w-[1600px] px-6 md:px-10">
                {/* Kicker */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.8 }}
                    className="flex items-center gap-3 mb-8 md:mb-12"
                >
                    <span className="w-8 h-px" style={{ background: "var(--pink)" }} />
                    <span className="font-body text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--pink)" }}>
                        {kicker}
                    </span>
                </motion.div>

                {/* Headline */}
                <h1 className="font-serif tracking-tight text-[11vw] md:text-[8vw] lg:text-[6.6vw] leading-[0.94] mb-6" style={{ color: "var(--ink-1)" }}>
                    <div>
                        <RevealLine delay={0}>{lines[0]}</RevealLine>
                    </div>
                    <div className="relative">
                        <RevealLine delay={0.15}>{lines[1]}</RevealLine>
                    </div>
                    <div className="italic" style={{ color: "var(--pink)" }}>
                        <RevealLine delay={0.3}>{lines[2]}</RevealLine>
                    </div>
                </h1>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mt-8 md:mt-10 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 2.6, duration: 0.9 }}
                        className="md:col-span-5"
                    >
                        <p className="font-body text-[15px] md:text-[17px] leading-[1.55] max-w-md" style={{ color: "var(--ink-2)" }}>
                            {tagline}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-6">
                            <a
                                href="tel:+18542524440"
                                data-testid={HOME.heroCallBtn}
                                className="btn-pink inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold tracking-wide"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                {t("hero.call")}
                            </a>
                            <a
                                href={`https://wa.me/1${(settings?.whatsapp || "8432510689").replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={HOME.heroWhatsappBtn}
                                className="btn-outline inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold tracking-wide"
                            >
                                {t("hero.whatsapp")} · {settings?.whatsapp_display}
                            </a>
                        </div>

                        <div className="mt-10 flex items-center gap-6 text-[12px] uppercase tracking-[0.22em]" style={{ color: "var(--ink-mute)" }}>
                            <div>
                                <div style={{ color: "var(--ink-1)" }} className="font-serif italic normal-case tracking-normal text-lg">
                                    Karina & family
                                </div>
                                <div>{t("hero.since")}</div>
                            </div>
                            <div className="w-px h-8" style={{ background: "rgba(46,16,37,0.2)" }} />
                            <div>
                                <div style={{ color: "var(--ink-1)" }} className="font-serif italic normal-case tracking-normal text-lg">
                                    Myrtle Beach
                                </div>
                                <div>3rd Ave S · SC</div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Kinetic banner + floating decor */}
                    <div className="md:col-span-7 relative min-h-[360px] md:min-h-[500px]" style={{ perspective: 1400 }}>
                        {/* Big kinetic banner */}
                        <motion.div
                            style={{ y: yBanner, rotate: rotBanner, scale: scaleBanner, rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
                            initial={{ opacity: 0, scale: 0.9, y: 40 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ delay: 1.9, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                            className="relative w-full aspect-[2/1] rounded-[36px] overflow-hidden"
                            data-testid={HOME.heroLogoMain}
                        >
                            <video
                                ref={videoRef}
                                poster={LOGO_HERO}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="auto"
                                onError={(e) => {
                                    // if the browser can't decode, hide the video and keep the poster fallback
                                    e.currentTarget.style.display = "none";
                                }}
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ transform: "translateZ(20px)", filter: "saturate(1.2) contrast(1.06) brightness(1.03)" }}
                                aria-label="Piñatas con Karina promo video"
                            >
                                <source src={HERO_VIDEO_MP4} type="video/mp4" />
                            </video>
                            {/* Glare highlight following mouse — extra subtle */}
                            <motion.div
                                aria-hidden
                                style={{
                                    background: `radial-gradient(500px circle at ${glareX} ${glareY}, rgba(255,255,255,0.10), transparent 65%)`,
                                }}
                                className="pointer-events-none absolute inset-0 mix-blend-screen opacity-50"
                            />
                            {/* frame outline */}
                            <div className="pointer-events-none absolute inset-0 rounded-[36px]" style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.5), 0 40px 80px -30px rgba(232,58,116,0.45)" }} />
                            {/* tiny badge */}
                            <span className="absolute top-4 left-4 font-body text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full backdrop-blur-md" style={{ background: "rgba(255,255,255,0.6)", color: "var(--ink-1)" }}>
                                the atelier · est. 2018
                            </span>

                            {/* Sound toggle */}
                            <motion.button
                                type="button"
                                data-testid={HOME.heroSoundToggle}
                                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                                aria-label={muted ? (lang === "es" ? "Activar sonido" : "Unmute video") : (lang === "es" ? "Silenciar" : "Mute video")}
                                aria-pressed={!muted}
                                whileHover={{ scale: 1.08 }}
                                whileTap={{ scale: 0.94 }}
                                className="absolute top-4 right-4 flex items-center gap-2 pl-3 pr-4 py-2 rounded-full backdrop-blur-md font-body text-[11px] uppercase tracking-[0.22em] font-semibold shadow-lg"
                                style={{ background: muted ? "rgba(255,255,255,0.85)" : "var(--pink)", color: muted ? "var(--ink-1)" : "#fff", border: "1px solid rgba(255,255,255,0.6)" }}
                            >
                                {muted ? <VolumeX size={14} strokeWidth={2.4} /> : <Volume2 size={14} strokeWidth={2.4} />}
                                <span>{muted ? (lang === "es" ? "Toca para escuchar" : "Tap for music") : (lang === "es" ? "Silenciar" : "Mute")}</span>
                                {muted && (
                                    <motion.span
                                        aria-hidden
                                        animate={{ scale: [1, 1.6, 1], opacity: [0.6, 0, 0.6] }}
                                        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                                        className="absolute inset-0 rounded-full"
                                        style={{ boxShadow: "0 0 0 2px var(--pink)" }}
                                    />
                                )}
                            </motion.button>
                        </motion.div>

                        {/* Floating balloon 1 (pink) */}
                        <FloatDecor delay={2.1} yShift={-140} xDrift={-30} rotShift={-15} floatDur={5.5} scrollY={scrollYProgress} className="-top-8 -left-6 md:-top-14 md:-left-14" style={{ zIndex: 3 }}>
                            <svg width="90" height="120" viewBox="0 0 90 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <ellipse cx="45" cy="45" rx="38" ry="45" fill="#E83A74" />
                                <ellipse cx="34" cy="30" rx="8" ry="12" fill="#FFB3CC" opacity="0.7" />
                                <path d="M45 90 L42 100 L48 100 Z" fill="#B02A5A" />
                                <path d="M45 100 Q40 108 45 118 Q50 108 45 100" stroke="#8B5FBF" strokeWidth="1.2" fill="none" />
                            </svg>
                        </FloatDecor>

                        {/* Floating balloon 2 (teal) */}
                        <FloatDecor delay={2.3} yShift={-100} xDrift={20} rotShift={10} floatDur={6.5} scrollY={scrollYProgress} className="top-10 -right-4 md:-right-10" style={{ zIndex: 3 }}>
                            <svg width="70" height="94" viewBox="0 0 90 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <ellipse cx="45" cy="45" rx="38" ry="45" fill="#0B7A75" />
                                <ellipse cx="34" cy="30" rx="8" ry="12" fill="#A3E4D7" opacity="0.7" />
                                <path d="M45 90 L42 100 L48 100 Z" fill="#0B4C48" />
                                <path d="M45 100 Q52 108 45 118 Q38 108 45 100" stroke="#EAB308" strokeWidth="1.2" fill="none" />
                            </svg>
                        </FloatDecor>

                        {/* Floating star */}
                        <FloatDecor delay={2.5} yShift={-80} xDrift={-15} rotShift={90} floatDur={7} scrollY={scrollYProgress} className="-bottom-2 left-1/4" style={{ zIndex: 3 }}>
                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                                <path d="M30 4 L36 22 L55 22 L40 33 L46 52 L30 41 L14 52 L20 33 L5 22 L24 22 Z" fill="#EAB308" stroke="#8A6B00" strokeWidth="1.5" strokeLinejoin="round" />
                            </svg>
                        </FloatDecor>

                        {/* Floating heart */}
                        <FloatDecor delay={2.6} yShift={-120} xDrift={30} rotShift={-20} floatDur={5} scrollY={scrollYProgress} className="-bottom-4 right-8 md:right-16" style={{ zIndex: 3 }}>
                            <svg width="54" height="48" viewBox="0 0 60 54" fill="none">
                                <path d="M30 48 L6 26 A12 12 0 0 1 30 12 A12 12 0 0 1 54 26 Z" fill="#E83A74" stroke="#B02A5A" strokeWidth="1.5" strokeLinejoin="round" />
                            </svg>
                        </FloatDecor>

                        {/* Floating sparkle */}
                        <FloatDecor delay={2.4} yShift={-60} xDrift={-40} rotShift={180} floatDur={4} scrollY={scrollYProgress} className="top-1/3 -left-4 md:left-2" style={{ zIndex: 3 }}>
                            <svg width="34" height="34" viewBox="0 0 40 40" fill="none">
                                <path d="M20 2 L23 17 L38 20 L23 23 L20 38 L17 23 L2 20 L17 17 Z" fill="#8B5FBF" />
                            </svg>
                        </FloatDecor>

                        {/* Handwritten note */}
                        <motion.div
                            initial={{ opacity: 0, y: 30, rotate: -3 }}
                            animate={{ opacity: 1, y: 0, rotate: 6 }}
                            transition={{ delay: 2.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute -bottom-6 md:-bottom-4 right-1 md:right-6 max-w-[220px] anim-wiggle"
                            style={{ transformOrigin: "top right", zIndex: 4 }}
                        >
                            <div className="p-4 rounded-md" style={{ background: "#FFFDF5", border: "1px solid rgba(46,16,37,0.12)", boxShadow: "0 20px 30px -12px rgba(0,0,0,0.15)" }}>
                                <div className="font-hand text-2xl leading-tight" style={{ color: "var(--ink-1)" }}>
                                    “{lang === "es" ? "Hacemos realidad la fiesta que siempre imaginaste" : "We build the party you always imagined"}”
                                </div>
                                <div className="font-body text-[10px] uppercase tracking-[0.24em] mt-2" style={{ color: "var(--ink-mute)" }}>
                                    — Karina
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 3.2, duration: 0.8 }}
                    className="hidden md:flex absolute bottom-4 right-8 items-center gap-3 font-body text-[10px] uppercase tracking-[0.32em]"
                    style={{ color: "var(--ink-mute)" }}
                >
                    <div className="relative w-10 h-px" style={{ background: "currentColor" }}>
                        <motion.span
                            animate={{ x: [0, 30, 0] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -top-[3px] left-0 w-2 h-2 rounded-full"
                            style={{ background: "var(--pink)" }}
                        />
                    </div>
                    <span>{t("hero.scroll")}</span>
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
